import { Repeat } from "lucide-react";

export const DEMO_HEX = { poco: "#6366F1", bebedouro: "#F59E0B", caixa: "#06B6D4", contrato: "#10B981" };

export const UNITS = { days: ["dia", "dias"], weeks: ["semana", "semanas"], months: ["mês", "meses"], years: ["ano", "anos"] };

export const intervalLabel = (v, unit) => `${v} ${UNITS[unit]?.[Number(v) === 1 ? 0 : 1] || unit}`;

const FALLBACK = { id: "", label: "Serviço", short: "Serviço", hex: "#94A3B8", validity: "", value: 1, unit: "months", lead_days: 15, active: false, icon: Repeat };
const registry = {};

// Tenant's service catalog, loaded from the API; unknown ids fall back to a neutral type.
export const SERVICE_TYPES = new Proxy(registry, { get: (r, k) => r[k] || FALLBACK });

export const setServiceTypes = (list) => {
  Object.keys(registry).forEach((k) => delete registry[k]);
  list.forEach((t) => {
    registry[t.id] = { ...t, label: t.name, short: t.name, hex: t.color, value: t.interval_value, unit: t.interval_unit,
      validity: intervalLabel(t.interval_value, t.interval_unit), icon: Repeat };
  });
};

export const activeTypeIds = () => Object.keys(registry).filter((k) => registry[k].active);

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
export const formatMoney = (v) => (v == null || v === "" ? "—" : BRL.format(Number(v)));

export const parseMoney = (s) => {
  const t = String(s ?? "").trim().replace(/\s|R\$/g, "");
  if (!t) return null;
  return Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
};

export const moneyInput = (v) => (v == null ? "" : String(v).replace(".", ","));

const PER_YEAR = { days: 365, weeks: 52, months: 12, years: 1 };
export const visitsPerYear = (value, unit) => (PER_YEAR[unit] || 12) / Math.max(1, Number(value) || 1);

export const CONTRACT_STATUS = {
  ativo: ["Ativo", "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"],
  pausado: ["Pausado", "bg-amber-500/15 text-amber-300 border-amber-500/40"],
  cancelado: ["Cancelado", "bg-rose-500/15 text-rose-300 border-rose-500/40"],
};

export const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const ALERT_DAYS = 15;

export const addInterval = (iso, value, unit) => {
  if (unit === "days") return addDays(iso, Number(value));
  if (unit === "weeks") return addDays(iso, Number(value) * 7);
  return addMonths(iso, unit === "years" ? Number(value) * 12 : Number(value));
};

const pad = (n) => String(n).padStart(2, "0");

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const addMonths = (iso, months) => {
  const [y, m, d] = iso.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return target.toISOString().slice(0, 10);
};

export const daysBetween = (fromIso, toIso) => Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86400000);

export const formatDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

export const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const utc = (iso) => new Date(`${iso}T00:00:00Z`);

export const addDays = (iso, n) => {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export const weekdayOf = (iso) => WEEKDAYS[utc(iso).getUTCDay()];

export const mondayOf = (iso) => addDays(iso, -((utc(iso).getUTCDay() + 6) % 7));

export const mapsSearch = (address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export const telHref = (phone) => `tel:${phone.replace(/[^\d+]/g, "")}`;

export const whatsappNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "").replace(/^0+/, "");
  if (digits.length < 10) return null;
  return digits.length <= 11 ? `55${digits}` : digits;
};

const introOf = (c) => {
  const r = c?.responsible_name?.trim();
  const n = c?.company_name?.trim();
  if (r && n) return ` Aqui é ${r}, da ${n}.`;
  if (n) return ` Aqui é da ${n}.`;
  return r ? ` Aqui é ${r}.` : "";
};

export const renderTpl = (tpl, vars) => tpl.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m))
  .replace(/Aqui é , da /g, "Aqui é da ").replace(/, da \./g, ".").replace(/\s*Contato: \./g, "").replace(/\s{2,}/g, " ").trim();

const companyVars = (c) => ({ empresa: c?.company_name?.trim() || "", responsavel: c?.responsible_name?.trim() || "", telefone_empresa: c?.phone?.trim() || "" });

export const visitMessage = (s, c, tpl) => (tpl
  ? renderTpl(tpl, { ...companyVars(c), cliente: s.client_name, servico: SERVICE_TYPES[s.service_type].label.toLowerCase(),
    dia_semana: weekdayOf(s.execution_date).toLowerCase(), data: formatDate(s.execution_date) })
  : `Olá, ${s.client_name}!${introOf(c)} Passando para confirmar a visita de ${SERVICE_TYPES[s.service_type].label.toLowerCase()} agendada para ${weekdayOf(s.execution_date).toLowerCase()}, ${formatDate(s.execution_date)}. Podemos confirmar?${c?.phone?.trim() ? ` Contato: ${c.phone.trim()}.` : ""} Obrigado!`);

export const whatsappHref = (s, c, tpl) => {
  const n = whatsappNumber(s.phone);
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(visitMessage(s, c, tpl))}` : null;
};

export const greetingHref = (phone, who, c, tpl) => {
  const n = whatsappNumber(phone);
  const text = tpl ? renderTpl(tpl, { ...companyVars(c), cliente: who }) : `Olá, ${who}!${introOf(c)}`;
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(text)}` : null;
};

export const monthTotals = (list) => {
  const by = {};
  let missing = 0;
  list.forEach((s) => {
    if (s.price == null || s.price === "") missing += 1;
    const v = Number(s.price) || 0;
    const row = by[s.service_type] || (by[s.service_type] = { key: s.service_type, done: 0, planned: 0 });
    row[s.status === "realizado" ? "done" : "planned"] += v;
  });
  const rows = Object.values(by).map((r) => ({ ...r, label: SERVICE_TYPES[r.key]?.label || "Serviço", hex: SERVICE_TYPES[r.key]?.hex || "#94A3B8" }));
  return { rows, missing, done: rows.reduce((a, r) => a + r.done, 0), planned: rows.reduce((a, r) => a + r.planned, 0) };
};

export const monthLabel = (iso) => `${MONTHS[Number(iso.slice(5, 7)) - 1]}/${iso.slice(0, 4)}`;

export const statusOf = (s, today) => {
  if (s.status === "realizado") return { key: "realizado" };
  const days = daysBetween(today, s.execution_date);
  if (days < 0) return { key: "vencido", days: -days };
  if (days <= (SERVICE_TYPES[s.service_type].lead_days ?? ALERT_DAYS)) return { key: "breve", days };
  return { key: "agendado", days };
};

export const tint = (hex, alpha = "26") => ({ backgroundColor: `${hex}${alpha}`, borderColor: `${hex}55` });
